(() => {
  if (customElements.get('canvoil-reviews-resilience')) return;

  class CanvoilReviewsResilience extends HTMLElement {
    connectedCallback() {
      if (this.controller || this.finished || !this.closest('#canvoil-product-reviews')) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.observer = new MutationObserver(() => this.refresh());
      this.observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'height', 'width', 'style', 'class', 'hidden'] });
      if ('ResizeObserver' in window) this.resizeObserver = new ResizeObserver(() => this.scheduleCheck());
      window.addEventListener('message', (event) => {
        // Never read cross-origin content or accept another window's readiness.
        if (event.origin !== 'https://loox.io' || !this.frame || event.source !== this.frame.contentWindow) return;
        let message = event.data;
        if (typeof message === 'string') {
          try { message = JSON.parse(message); } catch (_) { return; }
        }
        if (message?.type !== 'loox:widget_visible' || message.payload?.widgetType !== 'product-reviews') return;
        this.visibleMessage = true;
        this.scheduleCheck();
      }, options);
      this.addEventListener('focusout', () => this.scheduleCheck(), options);
      this.refresh();
    }

    refresh() {
      if (!this.controller || this.finished) return;
      const widget = this.querySelector('#looxReviews');
      if (!widget) return;
      if (this.widget && widget !== this.widget) {
        this.feed?.remove();
        this.feed = null;
      }
      this.widget = widget;
      const feed = widget.querySelector('#loox-reviews-feed');
      if (feed?.querySelector('article.loox-review')) {
        if (this.feed && this.feed !== feed) this.feed.remove();
        this.feed = feed;
        // Move the original node after the zero-height widget. Both review anchors
        // remain above it, and Loox's innerHTML replacement cannot erase it.
        widget.after(feed);
      }
      const frame = widget.querySelector('iframe#looxReviewsFrame');
      const source = frame?.getAttribute('src');
      if (frame !== this.frame || source !== this.frameSource || !this.frameController) {
        const sameFrame = frame === this.frame && source === this.frameSource;
        this.frameController?.abort();
        this.frameController = new AbortController();
        this.resizeObserver?.disconnect();
        this.frame = frame;
        this.frameSource = source;
        if (!sameFrame) {
          this.visibleMessage = false;
        }
        this.stableHeight = null;
        if (frame) {
          frame.addEventListener('load', () => {
            if (frame !== this.frame) return;
            this.scheduleCheck();
          }, { signal: this.frameController.signal });
          this.resizeObserver?.observe(frame);
        }
      }
      this.scheduleCheck();
    }

    scheduleCheck() {
      if (!this.controller || this.finished || this.checkFrame) return;
      this.checkFrame = requestAnimationFrame(() => {
        this.checkFrame = null;
        this.checkReady();
      });
    }

    checkReady() {
      if (!this.feed || !this.frame?.isConnected || !this.visibleMessage) return;
      let url;
      try { url = new URL(this.frame.getAttribute('src'), location.href); } catch (_) { return; }
      if (url.origin !== 'https://loox.io' || !/^\/widget\/[^/]+\/reviews(?:\/|$)/.test(url.pathname)) return;
      const bounds = this.frame.getBoundingClientRect();
      if (bounds.height < 100 || bounds.width <= 0) { this.stableHeight = null; return; }
      for (let element = this.frame; element && element !== this.parentElement; element = element.parentElement) {
        const style = getComputedStyle(element);
        if (element.hidden || style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || Number(style.opacity || 1) === 0) return;
      }
      // Require two stable layout frames; no fixed timeout can erase a slow feed.
      const height = Math.round(bounds.height);
      if (this.stableHeight !== height) {
        this.stableHeight = height;
        this.scheduleCheck();
        return;
      }
      if (this.feed.contains(document.activeElement)) return;
      this.feed.remove();
      this.feed = null;
      this.finished = true;
      this.stop();
    }

    stop() {
      this.controller?.abort();
      this.controller = null;
      this.frameController?.abort();
      this.frameController = null;
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.checkFrame) cancelAnimationFrame(this.checkFrame);
      this.checkFrame = null;
    }

    disconnectedCallback() {
      this.stop();
    }
  }

  customElements.define('canvoil-reviews-resilience', CanvoilReviewsResilience);
})();
